<template>
  <PageContainer title="Listar Veículos">
    <Box class="veiculos-container" space="medium">
      <div class="header">
        <Search legend="Procurar Veículo" placeholder="Insira placa do veículo" :disabled="loading"
          @submit:search="submitSearch" />

        <div class="buttons">
          <!-- O Detran não disponibiliza a consulta de veículos para instrutor autônomo -->
          <Button v-if="!isAutonomo" type="button" @handle:click="updateVeiculos" text="Atualizar veículos"
            color="secondary" :disabled="loading">
            <Cached :size="18" />
          </Button>

          <Button v-if="!isAutonomo || canUseIntegracaoDetran" type="button" @handle:click="openNovoVeiculoModal"
            text="Novo veículo" color="primary" :disabled="loading">
            <PlusCircleOutline :size="18" />
          </Button>

          <Button v-if="canAssociarVeiculosAutonomos" type="button" @handle:click="openAssociarVeiculosModal"
            text="Cadastrar veículo de autônomo no Detran" color="primary" :disabled="loading">
            <PlusCircleOutline :size="18" />
          </Button>

          <Button type="button" @handle:click="openExportModal" text="Exportar" color="primary" :fill="false"
            :disabled="loading">
            <TrayArrowDown :size="18" />
          </Button>
        </div>
      </div>

      <div v-if="loading" class="d-flex justify-content-center">
        <component :animation-duration="1000" :is="'SpringSpinner'" :color="'#1C2D5A'" :size="80" class="mx-2">
        </component>
      </div>

      <div v-else-if="veiculos.length > 0 && !loading" class="table-container">
        <div class="table-header">
          <div class="core-badge">
            <CarOutline :size="18" />

            <span class="text">
              Total de veículos: <span>{{ totalVeiculos }}</span>
            </span>
          </div>

          <Paginate :total-pages="paginationData.lastPage" :current-page="paginationData.currentPage"
            @page-changed="fetchVeiculos" />
        </div>

        <Table :headers="isAutonomo ? ['Status', 'Placa', 'Categoria', 'Detran', 'Ações'] : ['Status', 'Placa', 'Categoria', 'Ações']">
          <tr v-for="veiculo in veiculos" :key="veiculo.id_aluno">
            <td>
              <Badge v-if="veiculo.flg_ativo" text="Ativo" color="success" />
              <Badge v-else text="Inativo" color="danger" />
            </td>
            <td>{{ veiculo.placa }}</td>
            <td>{{ veiculo.categoria.categoria_cnh }}</td>
            <td v-if="isAutonomo">
              <Badge v-if="veiculo.principal === true" text="Principal" color="success" />
              <template v-else-if="veiculo.principal === false">
                <Badge text="Não principal" color="warning" />
                <small class="periodo">{{ formatarPeriodo(veiculo) }}</small>
              </template>
              <Badge v-else text="Não associado" color="danger" />
            </td>
            <td>
              <div class="actions">
                <div v-if="veiculo.flg_ativo">
                  <IconButton @handle:click="updateStatus(veiculo.id_veiculo, 'desativar')" color="danger"
                    title="Desativar usuário" :disabled="loadingActions">
                    <LockOutline :size="19" />
                  </IconButton>
                </div>
                <div v-else>
                  <IconButton @handle:click="updateStatus(veiculo.id_veiculo, 'ativar')" color="success"
                    title="Ativar usuário" :disabled="loadingActions">
                    <LockOpenOutline :size="19" />
                  </IconButton>
                </div>
                <div>
                  <IconButton @handle:click="openInfoModal(veiculo.id_veiculo)" color="secondary"
                    title="Ver informações do usuário" :disabled="loadingActions">
                    <InformationOutline :size="19" />
                  </IconButton>
                </div>
                <div>
                  <IconButton @handle:click="toEdit(veiculo.id_veiculo)" color="primary" title="Atualizar veículo"
                    :disabled="!veiculo.flg_ativo || loadingActions">
                    <PencilOutline :size="19" />
                  </IconButton>
                </div>
              </div>
            </td>
          </tr>
        </Table>
      </div>
    </Box>

    <ExportModal ref="exportModal" @submit:export="submitExport" :loading="exportLoading" />

    <InfoModal ref="infoModal" />

    <AssociarVeiculosModal v-if="canUseIntegracaoDetran || canAssociarVeiculosAutonomos" ref="associarVeiculosModal"
      :selecionarAutonomo="canAssociarVeiculosAutonomos" @veiculoCadastrado="fetchVeiculos" />
    <NovoVeiculoModal v-if="!canUseIntegracaoDetran" ref="novoVeiculoModal" @veiculoCadastrado="fetchVeiculos" />
  </PageContainer>
</template>

<script>
import * as spinners from 'epic-spinners'
import urlApi from '../../router/urlApi'
import _export from '../../services/export'
import PageContainer from '../core/PageContainer'
import Box from '../core/Box'
import Search from '../core/Search'
import Button from '../core/Button'
import Input from '../core/Input'
import Select from '../core/Select'
import Period from '../core/Period'
import Badge from '../core/Badge'
import Table from '../core/Table'
import Paginate from '../core/Paginate'
import IconButton from '../core/IconButton'
import CloseCircleOutline from 'icons/CloseCircleOutline'
import Magnify from 'icons/Magnify'
import TrayArrowDown from 'icons/TrayArrowDown'
import LockOutline from 'icons/LockOutline'
import LockOpenOutline from 'icons/LockOpenOutline'
import InformationOutline from 'icons/InformationOutline'
import PencilOutline from 'icons/PencilOutline'
import Cached from 'icons/Cached'
import CarOutline from 'icons/CarOutline'
import PlusCircleOutline from 'icons/PlusCircleOutline'
import ExportModal from '../export/ExportModal'
import InfoModal from './InfoModal'
import NovoVeiculoModal from './NovoVeiculoModal'
import AssociarVeiculosModal from './AssociarVeiculosModal'
import { useAuth } from '@/composables/useAuth'

export default {
  name: 'ListagemVeiculos',
  components: {
    ...spinners,
    CloseCircleOutline,
    Magnify,
    TrayArrowDown,
    LockOutline,
    LockOpenOutline,
    InformationOutline,
    PencilOutline,
    Cached,
    CarOutline,
    PlusCircleOutline,

    PageContainer,
    Box,
    Search,
    Button,
    Input,
    Select,
    Period,
    Badge,
    Table,
    Paginate,
    IconButton,
    ExportModal,
    InfoModal,
    NovoVeiculoModal,
    AssociarVeiculosModal,
  },
  data () {
    return {
      search: '',
      veiculos: [],
      totalVeiculos: '',
      paginationData: {
        currentPage: 1,
        lastPage: 0,
        total: 0,
      },
      badges: {
        ativos: '',
        inativos: '',
      },
      loading: false,
      loadingActions: false,
      exportLoading: false,
      isAutonomo: useAuth().isAutonomo,
      canUseIntegracaoDetran: useAuth().canUseIntegracaoDetran,
      canAssociarVeiculosAutonomos: useAuth().canAssociarVeiculosAutonomos,
    }
  },
  async mounted () {
    await this.fetchVeiculos()
  },
  methods: {
    async fetchVeiculos (page = 1) {
      this.loading = true

      const params = {
        search: this.search,
        page,
      }

      this.filters = params

      await this.$http.get(`${urlApi}/veiculos/ordenar`, {
        params,
      })
        .then(response => {
          const data = response.body

          this.veiculos = data.data

          if (this.veiculos.length < 1) {
            this.toast('Nenhum resultado encontrado!')
          }

          this.paginationData = {
            currentPage: data.current_page,
            lastPage: data.last_page,
            total: data.total,
          }

          this.totalVeiculos = data.total
        })
        .catch(() => {
          this.toast('Erro ao buscar veículos.')
        })
        .finally(() => {
          this.loading = false
        })
    },
    async updateVeiculos () {
      this.loading = true

      await this.$http.get(`${urlApi}/getran/atualizar/veiculos`)
        .then(response => {
          this.fetchVeiculos()
        }).catch(err => {
          this.toast(err.body.dados)

          this.loading = false
        })
    },
    async submitSearch (e) {
      this.search = e

      await this.fetchVeiculos()
    },
    openExportModal () {
      this.$refs.exportModal.open()
    },
    async submitExport (e) {
      this.exportLoading = true

      if (e.format === 'pdf') {
        await this.$http.get(`${urlApi}/veiculos/pdf/${e.initialDate}/${e.finalDate}`)
          .then(response => {
            _export.downloadPDF(response.body.dados, 'veiculos')
          })
          .catch(() => {
            this.toast('Erro ao exportar o arquivo.')
          })
          .finally(() => {
            this.exportLoading = false
          })
      } else {
        await this.$http.get(`${urlApi}/veiculos/xlsx/${e.initialDate}/${e.finalDate}`)
          .then(response => {
            _export.downloadCSV(response.body.dados, 'veiculos')
          })
          .catch(() => {
            this.toast('Erro ao exportar o arquivo.')
          })
          .finally(() => {
            this.exportLoading = false
          })
      }

      this.$refs.exportModal.close()
    },
    async updateStatus (id, status) {
      this.loadingActions = true

      await this.$http.patch(`${urlApi}/veiculos/${id}/${status}`)
        .then(async response => {
          this.toast('Status atualizado!')

          await this.fetchVeiculos()
        })
        .catch(() => {
          this.toast('Erro ao atualizar status do veículo.')
        })
        .finally(() => {
          this.loadingActions = false
        })
    },
    openInfoModal (id) {
      this.$refs.infoModal.open(id)
    },
    toEdit (id) {
      this.$router.push({ name: 'editar-veiculo', params: { idVeiculo: id } })
    },
    openNovoVeiculoModal () {
      // Instrutor autônomo cadastra os veículos direto no Detran
      if (this.canUseIntegracaoDetran) {
        this.$refs.associarVeiculosModal.open()
      } else {
        this.$refs.novoVeiculoModal.open()
      }
    },
    openAssociarVeiculosModal () {
      this.$refs.associarVeiculosModal.open()
    },
    formatarPeriodo (veiculo) {
      const data = valor => (valor ? valor.substring(0, 10).split('-').reverse().join('/') : '?')
      return `${data(veiculo.data_inicio_utilizacao)} a ${data(veiculo.data_fim_utilizacao)}`
    },
    toast (text) {
      this.$toasted.show(text, {
        icon: 'fa-times',
        position: 'top-right',
        duration: 5000,
        fullWidth: false
      })
    },
  }
}
</script>

<style lang="scss" scoped>
.periodo {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: #606264;
}

.veiculos-container {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;

  .header {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: flex-end;
    gap: 1.5rem;

    form {
      flex: 1;
      max-width: 24.125rem;
    }

    .buttons {
      display: flex;
      align-items: center;
      gap: 1rem;
    }
  }

  .table-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .table-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 1rem;

      .core-badge {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        color: #202224;

        .text {
          margin-top: 0.3rem;
          font-size: 0.75rem;
          font-weight: 400;

          span {
            font-weight: 800;
          }
        }
      }
    }

    .actions {
      display: flex;
      align-items: center;

      div {
        padding: 0 0.5rem;
        display: flex;
        justify-content: center;

        &:not(:first-child) {
          border-left: 1px solid #ECECEC;
        }

        &:not(:last-child) {
          border-right: 1px solid #ECECEC;
        }
      }
    }
  }

  .actions {
    align-self: flex-end;
  }
}
</style>
